import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { BORDER_RADIUS } from "app/theme";
import {
  actionStatusAccent,
  actionStatusRank,
  formatActionDueMonth,
  normalizeActionStatus,
  splitActionTitle,
} from "common/components/actionStatus";
import DetailDialog from "common/components/DetailDialog";
import { getFormattedCategoryName } from "features/programmesActions/ActionList";
import { useActionEvidence } from "model";
import { useEffect, useRef, useState } from "react";
import {
  AnimalPerformanceCard,
  BiodiversityCard,
  CarbonFootprintCard,
  MilkQualityCard,
  WaterSoilCard,
} from "../AssessmentCards";
import type { FarmerActionData } from "../hooks/useInsightsDashboardQueries";
import {
  useFarmerActions,
  useProgrammeInfo,
} from "../hooks/useInsightsDashboardQueries";
import type { InsightCategory } from "../InsightDetailDialog";
import InsightDetailDialog from "../InsightDetailDialog";
import ActionDetailView from "./ActionDetailView";
import FarmerFilesTable from "./FarmerFilesTable";

// ─── Actions Table ──────────────────────────────────────────────

function ActionsTable({
  farmerGuid,
  onActionSelect,
  evidenceCountOverrides,
}: {
  farmerGuid: string;
  onActionSelect: (guid: string) => void;
  evidenceCountOverrides: Record<string, number>;
}) {
  const { data, isLoading } = useFarmerActions(farmerGuid);
  // Sort: done first, then on-track, then submitted.
  const actions = [...(data?.data ?? [])].sort(
    (a, b) =>
      actionStatusRank(normalizeActionStatus(a["SubmittedActions.status"])) -
      actionStatusRank(normalizeActionStatus(b["SubmittedActions.status"]))
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: BORDER_RADIUS.small + "px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 3,
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b" }}
        >
          Submitted Actions
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ p: 1 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ p: "10px 14px", mb: 0.5 }}>
              <Skeleton variant="text" width={120} height={12} />
              <Skeleton variant="text" width={260} height={18} />
            </Box>
          ))}
        </Box>
      ) : actions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No actions submitted yet
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1 }}>
          {actions.map((action) => {
            const guid = action["SubmittedActions.guid"];
            const evidenceCount =
              evidenceCountOverrides[guid] ??
              (Number(action["SubmittedActions.evidenceCount"]) || 0);
            const status = normalizeActionStatus(
              action["SubmittedActions.status"]
            );
            const accent = actionStatusAccent(status);
            const [lead, clarifier] = splitActionTitle(
              action["SubmittedActions.action"] ?? ""
            );
            const submitted = formatActionDueMonth(
              action["SubmittedActions.submissionDate"]
            );
            const category =
              getFormattedCategoryName(
                parseInt(action["SubmittedActions.categoryId"] ?? "0", 10)
              ) || action["SubmittedActions.category"];

            return (
              <Box
                key={guid}
                onClick={() => onActionSelect(guid)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  columnGap: 1.5,
                  alignItems: "start",
                  p: "10px 14px",
                  mb: 0.5,
                  borderRadius: BORDER_RADIUS.small + "px",
                  cursor: "pointer",
                  backgroundColor:
                    status === "completed"
                      ? "rgba(220, 252, 231, 0.35)"
                      : status === "on-track"
                      ? "rgba(224, 242, 254, 0.35)"
                      : "transparent",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                  "&:last-of-type": { mb: 0 },
                }}
              >
                <Box
                  aria-label={`Status: ${status}`}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: accent ?? "#d4d4d8",
                    mt: "16px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  {category && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#a1a1aa",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        lineHeight: 1.4,
                      }}
                    >
                      {category}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#18181b",
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {lead}
                  </Typography>
                  {clarifier && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#a1a1aa",
                        fontSize: 12,
                        lineHeight: 1.4,
                        mt: 0.25,
                      }}
                    >
                      {clarifier}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: "12px",
                    flexShrink: 0,
                  }}
                >
                  {submitted && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "999px",
                        bgcolor: "#f4f4f5",
                        color: "#52525b",
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Submitted {submitted}
                    </Box>
                  )}
                  {evidenceCount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#16a34a",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 14 }} />
                      {evidenceCount}
                    </Box>
                  )}
                  <IconButton size="small" sx={{ p: 0 }}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ─── Main farmer detail dialog ──────────────────────────────────

export default function FarmerDetailDialog({
  open,
  farmerGuid,
  farmerName,
  onClose,
}: {
  open: boolean;
  farmerGuid: string | null;
  farmerName: string | null;
  onClose: () => void;
}) {
  const [detailCategory, setDetailCategory] = useState<InsightCategory | null>(
    null
  );
  const [selectedActionGuid, setSelectedActionGuid] = useState<string | null>(
    null
  );
  const [evidenceCountOverrides, setEvidenceCountOverrides] = useState<
    Record<string, number>
  >({});
  const { data: programmeInfo } = useProgrammeInfo();
  const { data: farmerActionsData } = useFarmerActions(farmerGuid ?? undefined);
  const programmeType =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] ?? "BEEF";
  const isDairy = programmeType === "DAIRY";

  const selectedAction: FarmerActionData | undefined = selectedActionGuid
    ? farmerActionsData?.data?.find(
        (a) => a["SubmittedActions.guid"] === selectedActionGuid
      )
    : undefined;

  // Track evidence count from the live evidence list for the currently open action
  const { data: currentEvidence } = useActionEvidence(
    selectedActionGuid ?? undefined
  );
  const lastEvidenceRef = useRef<{ guid: string; count: number } | null>(null);

  useEffect(() => {
    if (selectedActionGuid && currentEvidence) {
      lastEvidenceRef.current = {
        guid: selectedActionGuid,
        count: currentEvidence.length,
      };
    }
  }, [selectedActionGuid, currentEvidence]);

  const handleBackFromAction = () => {
    const last = lastEvidenceRef.current;
    if (last) {
      setEvidenceCountOverrides((prev) => ({
        ...prev,
        [last.guid]: last.count,
      }));
    }
    setSelectedActionGuid(null);
  };

  // Reset internal navigation state when dialog closes
  useEffect(() => {
    if (!open) {
      setDetailCategory(null);
      setSelectedActionGuid(null);
    }
  }, [open]);

  if (!farmerGuid) return null;

  return (
    <>
      <DetailDialog
        open={open}
        onClose={onClose}
        title={farmerName ?? "Farmer Details"}
        contentSx={{ backgroundColor: "#F7FCF1" }}
      >
        <Box
          sx={{
            maxWidth: 800,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 3,
          }}
        >
          <CarbonFootprintCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("carbon-footprint")}
          />
          <BiodiversityCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("biodiversity")}
          />
          <WaterSoilCard
            farmerGuid={farmerGuid}
            onClick={() => setDetailCategory("water-soil")}
          />
          {isDairy ? (
            <MilkQualityCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("milk-quality")}
            />
          ) : (
            <AnimalPerformanceCard
              farmerGuid={farmerGuid}
              onClick={() => setDetailCategory("animal-performance")}
            />
          )}
        </Box>

        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          <ActionsTable
            farmerGuid={farmerGuid}
            onActionSelect={setSelectedActionGuid}
            evidenceCountOverrides={evidenceCountOverrides}
          />
          <FarmerFilesTable farmerGuid={farmerGuid} />
        </Box>
      </DetailDialog>

      <InsightDetailDialog
        open={detailCategory !== null}
        category={detailCategory}
        onClose={onClose}
        onBack={() => setDetailCategory(null)}
        farmerGuid={farmerGuid}
      />

      <DetailDialog
        open={selectedAction !== undefined}
        onClose={onClose}
        onBack={handleBackFromAction}
        title={selectedAction?.["SubmittedActions.action"] ?? "Action Details"}
      >
        {selectedAction && (
          <ActionDetailView
            action={selectedAction}
            farmerGuid={farmerGuid ?? undefined}
          />
        )}
      </DetailDialog>
    </>
  );
}
